import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, UserPlus, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import Captcha from "./Captcha";

export default function SuperadminSignup() {
  const [form, setForm] = useState({ name: "", username: "", email: "", mobile: "", password: "", confirm: "" });
  const [captcha, setCaptcha] = useState({ captcha_id: "", answer: "" });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [capKey, setCapKey] = useState(0);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) { setError("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/signup", {
        name: form.name, username: form.username, email: form.email, mobile: form.mobile,
        password: form.password, captcha_id: captcha.captcha_id, captcha_answer: captcha.answer,
      });
      setDone(true);
    } catch (err) {
      setError(errMsg(err));
      setCapKey((k) => k + 1);
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, type = "text") => (
    <div key={key}>
      <label htmlFor={`su-${key}`} className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">{label}</label>
      <input id={`su-${key}`} data-testid={`signup-${key}`} type={type} required value={form[key]} onChange={set(key)}
        className="w-full rounded-md border border-gray-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-saffron-500/50" />
    </div>
  );

  return (
    <div className="grain flex min-h-screen items-center justify-center bg-navy px-4 py-10" data-testid="superadmin-signup-page">
      <SEO title="Superadmin Signup" />
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white p-8 shadow-2xl sm:p-10">
        <div className="flex flex-col items-center text-center">
          <img src="/logo.png" alt="Sahaj Sarathi Foundation logo" className="h-16 w-16 rounded-full bg-white object-contain ring-2 ring-saffron-500/50" />
          <h1 className="mt-4 font-display text-xl font-bold tracking-tight text-navy">SAHAJ SARATHI FOUNDATION</h1>
          <p className="mt-1 text-xs font-bold uppercase tracking-[0.2em] text-saffron-600">Create Account</p>
        </div>
        {done ? (
          <div className="mt-8 text-center" data-testid="signup-success">
            <CheckCircle2 className="mx-auto h-14 w-14 text-brandgreen" strokeWidth={1.5} />
            <h2 className="mt-4 font-display text-xl font-bold text-navy">Request Submitted</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Your account is <b>pending approval</b>. An existing Superadmin must approve it before you can sign in.
            </p>
            <Link to="/superadmin/login" className="btn-primary mt-6 inline-flex" data-testid="signup-goto-login">Go to Sign In</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="superadmin-signup-form">
            {field("name", "Full Name")}
            {field("username", "Username")}
            {field("email", "Email", "email")}
            {field("mobile", "Mobile Number", "tel")}
            <div>
              <label htmlFor="su-password" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Password</label>
              <div className="relative">
                <input id="su-password" data-testid="signup-password" type={showPw ? "text" : "password"} required minLength={8} value={form.password} onChange={set("password")}
                  className="w-full rounded-md border border-gray-200 px-4 py-2.5 pr-11 text-sm outline-none focus:ring-2 focus:ring-saffron-500/50" />
                <button type="button" onClick={() => setShowPw(!showPw)} aria-label="Show or hide password" data-testid="signup-show-password"
                  className="absolute right-3 top-2.5 text-slate-400 hover:text-navy">{showPw ? <EyeOff size={18} /> : <Eye size={18} />}</button>
              </div>
            </div>
            {field("confirm", "Confirm Password", showPw ? "text" : "password")}
            <Captcha key={capKey} onChange={setCaptcha} />
            {error && <p className="rounded-lg bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-600" data-testid="signup-error">{error}</p>}
            <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-50" data-testid="signup-submit">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={15} />} Create Account
            </button>
          </form>
        )}
        <Link to="/superadmin/login" className="mt-6 block text-center text-xs font-semibold text-slate-400 hover:text-saffron-600" data-testid="signup-back-login">← Back to Sign In</Link>
      </div>
    </div>
  );
}
